import { memo, useCallback, useMemo, useState } from 'react'
import { LoadingState } from '@highlight/ui'
import { Virtuoso } from 'react-virtuoso'
import { View } from '../../components/view'
import { ViewActionCell } from '../../components/view-action-cell'
import { ViewGroupLabel } from '../../components/view-group-label'
import { toTimeframeRows, timeframeRowKey } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import { MOUNT_ALL_VIEWPORT_BY } from '@renderer/utils/virtualization-mount-all'
import { useSidepanelFindInPageOpen } from '../../atoms'
import { ActionsViewHeader } from './nested/header'
import { useActionsViewModel } from './use-actions-view-model'
import type { StagedAction } from '../../hooks/use-staged-actions'

const LOADING_MESSAGES = ['Checking for actions…', 'Reviewing your tasks…', 'Getting things ready…', 'Almost there…']

const NORMAL_VIEWPORT_BY = { top: 200, bottom: 200 }

const ActionRow = memo(function ActionRow({ action }: { action: StagedAction }) {
  return (
    <div className="pb-[2px]">
      <ViewActionCell action={action} sourceLabel="Outbox" hoverPreview />
    </div>
  )
})

/** Full-list View for staged, completed, and archived actions. */
export function ActionsView() {
  const model = useActionsViewModel()
  const findInPageOpen = useSidepanelFindInPageOpen()

  // Virtuoso needs the scroll element as a value, and View.Body owns it.
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)
  const captureScrollElement = useCallback((element: HTMLDivElement | null) => setScrollElement(element), [])

  // Grouped by day, same as History and Reminders (Figma `7652:21916`). Bucketed
  // by `createdAt` for the same reason the model sorts by it: `updatedAt` moves
  // when an action runs, which would re-label a yesterday draft into "Today" the
  // instant it succeeded and relocate the row.
  const rows = useMemo(() => toTimeframeRows(model.actions, (action) => new Date(action.createdAt)), [model.actions])

  const loading = (
    <LoadingState className="w-full px-3 pb-3 pt-1">
      <LoadingState.Spinner />
      <LoadingState.Text messages={LOADING_MESSAGES} />
    </LoadingState>
  )

  return (
    <View>
      <ActionsViewHeader model={model} />

      <View.Body bottomInset="list" onScrollElement={captureScrollElement}>
        {model.isLoading ? (
          loading
        ) : rows.length === 0 ? (
          <View.Empty searchQuery={model.searchQuery}>
            {model.filter === 'archived' ? 'No archived actions' : 'No actions'}
          </View.Empty>
        ) : scrollElement ? (
          <Virtuoso
            customScrollParent={scrollElement}
            data={rows}
            computeItemKey={(_index, row) => timeframeRowKey(row, (action) => action.id)}
            itemContent={(_index, row) =>
              row.kind === 'label' ? (
                <ViewGroupLabel isFirst={row.isFirst}>{row.label}</ViewGroupLabel>
              ) : (
                <ActionRow action={row.item} />
              )
            }
            increaseViewportBy={findInPageOpen ? MOUNT_ALL_VIEWPORT_BY : NORMAL_VIEWPORT_BY}
          />
        ) : (
          // The body reports its scroll element on mount, so it's null only for
          // the first render before commit. Show the loading state for that
          // single frame instead of mounting every (heavy) row just to unmount
          // them once Virtuoso takes over.
          loading
        )}
      </View.Body>
    </View>
  )
}
